<template>
  <section
    class="home-quick-categories"
    aria-label="Categorías principales"
    data-aos="fade"
    data-aos-duration="600"
  >
    <div class="container">
      <div class="home-quick-categories__carousel">
        <button
          type="button"
          class="home-quick-categories__nav home-quick-categories__nav--prev"
          aria-label="Categorías anteriores"
          @click="scrollByCards(-1)"
          v-show="canScrollLeft"
        >
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>

        <div ref="scrollerRef" class="home-quick-categories__grid">
          <a
            v-for="(item, idx) in items"
            :key="item.label"
            :href="item.href"
            class="home-quick-categories__card"
            data-aos="fade"
            :data-aos-delay="String(80 + idx * 70)"
          >
            <span
              class="home-quick-categories__icon"
              :style="{ '--icon-url': `url(${item.icon})` }"
              aria-hidden="true"
            >
              <svg
                class="home-quick-categories__border"
                viewBox="0 0 150 150"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <rect
                  x="1.5"
                  y="1.5"
                  width="147"
                  height="147"
                  rx="28"
                  ry="28"
                  pathLength="1"
                />
              </svg>

              <span class="home-quick-categories__glow" aria-hidden="true"></span>
              <span class="home-quick-categories__glyph" aria-hidden="true"></span>
            </span>

            <span class="home-quick-categories__label fw-normal fs-5">
              {{ item.label }}
            </span>
          </a>
        </div>

        <button
          type="button"
          class="home-quick-categories__nav home-quick-categories__nav--next"
          aria-label="Categorías siguientes"
          @click="scrollByCards(1)"
          v-show="canScrollRight"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const scrollerRef = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

const scrollByCards = (direction) => {
  const el = scrollerRef.value
  if (!el) return

  const card = el.querySelector('.home-quick-categories__card')
  if (!card) return

  const styles = window.getComputedStyle(el)
  const gap = parseFloat(styles.gap || '0')
  const cardWidth = card.getBoundingClientRect().width

  el.scrollBy({
    left: direction * (cardWidth + gap),
    behavior: 'smooth',
  })
}

const updateScrollState = () => {
  const el = scrollerRef.value
  if (!el) return

  const maxScrollLeft = el.scrollWidth - el.clientWidth

  canScrollLeft.value = el.scrollLeft > 2
  canScrollRight.value = el.scrollLeft < maxScrollLeft - 2
}

const onScroll = () => updateScrollState()
const onResize = () => updateScrollState()

onMounted(async () => {
  await nextTick()
  updateScrollState()

  scrollerRef.value?.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  scrollerRef.value?.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
})

const items = [
  {
    label: 'Teléfonos',
    href: '/productos?category_name=Telefonos',
    icon: require('@/assets/icons/phones.svg'),
  },
  {
    label: 'Auriculares',
    href: '/productos?category_name=Auriculares',
    icon: require('@/assets/icons/headphones.svg'),
  },
  {
    label: 'Accesorios',
    href: '/productos?category_name=Accesorios',
    icon: require('@/assets/icons/accesories.svg'),
  },
  {
    label: 'Reparación',
    href: '/productos?category_name=Reparacion',
    icon: require('@/assets/icons/phone-repair.svg'),
  },
  {
    label: 'Regalería',
    href: '/productos?category_name=Regaleria',
    icon: require('@/assets/icons/regaleria.svg'),
  },
  {
    label: 'Parlantes',
    href: '/productos?category_name=Parlantes',
    icon: require('@/assets/icons/speaker.svg'),
  },
]
</script>

<style scoped>
.home-quick-categories {
  background: #000;
  padding: clamp(1.5rem, 4vw, 2.5rem) 0;
}

.home-quick-categories__carousel {
  position: relative;
}

.home-quick-categories__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: clamp(1rem, 2.5vw, 1.6rem);
}

.home-quick-categories__nav {
  display: none;
}

.home-quick-categories__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.85rem;
  color: #fff;
  text-decoration: none;
  outline: none;
  cursor: default;
  transition: transform 0.3s ease;
}

.home-quick-categories__card:focus-visible {
  transform: translateY(-3px);
}

.home-quick-categories__icon {
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  background:
    linear-gradient(145deg, rgba(58, 58, 58, 0.06), transparent 70%),
    #141414;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.07),
    0 10px 25px rgba(0, 0, 0, 0.25);
  transition:
    transform 0.25s ease,
    background-color 0.25s ease,
    box-shadow 0.25s ease;
}

.home-quick-categories__icon:hover,
.home-quick-categories__card:focus-visible .home-quick-categories__icon {
  box-shadow:
    inset 0 0 0 1px rgba(255, 86, 86, 0.18),
    0 14px 32px rgba(207, 54, 54, 0.12),
    0 18px 36px rgba(0, 0, 0, 0.38);
}

.home-quick-categories__border {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.home-quick-categories__border rect {
  fill: none;
  stroke: var(--primary-color);
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  filter: drop-shadow(0 0 4px rgba(255, 38, 38, 0.35));
}

.home-quick-categories__icon:hover .home-quick-categories__border rect,
.home-quick-categories__card:focus-visible .home-quick-categories__border rect {
  animation: quick-category-draw-border 0.85s ease forwards;
}

.home-quick-categories__glow {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(
    circle at 24% 14%,
    rgba(136, 136, 136, 0.22),
    transparent 42%
  );
  opacity: 0.45;
  transition: opacity 0.25s ease;
}

.home-quick-categories__icon:hover .home-quick-categories__glow,
.home-quick-categories__card:focus-visible .home-quick-categories__glow {
  opacity: 0.72;
}

.home-quick-categories__glyph {
  position: relative;
  z-index: 2;
  width: 54px;
  height: 54px;
  background-color: var(--primary-color);
  opacity: 0.95;

  mask-image: var(--icon-url);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;

  -webkit-mask-image: var(--icon-url);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;

  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.home-quick-categories__icon:hover .home-quick-categories__glyph,
.home-quick-categories__card:focus-visible .home-quick-categories__glyph {
  transform: scale(1.06);
  opacity: 1;
}

.home-quick-categories__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-align: center;
  transition: transform 0.25s ease;
}

.home-quick-categories__label::after {
  content: '';
  display: inline-block;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.2s ease,
    transform 0.25s ease;
}

.home-quick-categories__label::after {
  width: 0.5rem;
  height: 0.5rem;
  border-top: 2px solid var(--primary-color);
  border-right: 2px solid var(--primary-color);
  transform: translateY(-1px);
}

.home-quick-categories__icon:hover ~ .home-quick-categories__label,
.home-quick-categories__card:focus-visible .home-quick-categories__label {
  transform: none;
}

.home-quick-categories__icon:hover ~ .home-quick-categories__label::after,
.home-quick-categories__card:focus-visible .home-quick-categories__label::after {
  opacity: 1;
  transform: translateY(-50%);
}

@keyframes quick-category-draw-border {
  from {
    stroke-dashoffset: 1;
  }

  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-quick-categories__icon,
  .home-quick-categories__glyph,
  .home-quick-categories__glow,
  .home-quick-categories__label,
  .home-quick-categories__label::after {
    transition: none;
  }

  .home-quick-categories__icon:hover .home-quick-categories__border rect,
  .home-quick-categories__card:focus-visible .home-quick-categories__border rect {
    animation: none;
    stroke-dashoffset: 0;
  }
}

@media (max-width: 575.98px) {
  .home-quick-categories__carousel {
    padding-inline: 0;
  }

  .home-quick-categories__grid {
    display: flex;
    gap: 1.2rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0.25rem;
    scrollbar-width: none;
  }

  .home-quick-categories__grid::-webkit-scrollbar {
    display: none;
  }

  .home-quick-categories__card {
    flex: 0 0 44%;
    scroll-snap-align: start;
    align-items: stretch;
  }

  .home-quick-categories__nav {
    position: absolute;
    top: 50%;
    z-index: 5;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
  }

  .home-quick-categories__nav--prev {
    left: -0.25rem;
  }

  .home-quick-categories__nav--next {
    right: -0.25rem;
  }

  .home-quick-categories__icon {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    border-radius: 22px;
  }

  .home-quick-categories__border rect {
    rx: 22;
    ry: 22;
  }

  .home-quick-categories__glyph {
    width: 46px;
    height: 46px;
  }

  .home-quick-categories__label {
    font-size: 1rem;
  }
}
</style>